import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ClientPageLayout } from "@/components/client/ClientPageLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { addDays, format, startOfWeek, endOfWeek, addWeeks, subWeeks, isSameDay } from "date-fns";
import { ChevronLeft, ChevronRight, CalendarDays, Target, CheckCircle, ArrowRight, Activity, HeartPulse, Scale } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import ProfileIconLink from "@/components/ProfileIconLink";
import { useEnforceLegalApproval } from "@/hooks/use-enforce-legal";
import { getMyCurrentProgram, type ClientProgram } from "@/integrations/backend/programs";
import { labels } from "@/lib/onboarding-options";
import { getOnboarding } from "@/integrations/backend/onboarding";
import { getMyProfile } from "@/integrations/backend/profiles";
import { workoutFrequencyToWeeklyGoal } from "@/lib/weekly-goal";
import {
  buildWeeklySummariesSinceJoin,
  computeBestStreakWeeks,
  computeCurrentStreakWeeks,
  computeMetPercentage,
  type WeekSummary,
} from "@/lib/streaks";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createBodyWeight,
  createRestingHr,
  getGoalsSummary,
  getEnabledGoalMetrics,
  listAvailableGoalMetrics,
  listBodyWeight,
  listOptionalMetricEntries,
  listRestingHr,
  listRpe,
  createOptionalMetricEntry,
  type BodyWeightEntry,
  type GoalMetricCatalogItem,
  type GoalsSummary,
  type OptionalMetricEntry,
  type RestingHrEntry,
  type RpeEntry,
} from "@/integrations/backend/goals";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader as UIDialogHeader,
  DialogTitle as UIDialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DateRangeCalendar } from "@/components/ui/calendar";
import type { DateRange } from "react-day-picker";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import {
  fetchAssignedWorkoutReview,
  listWorkoutAssigned,
  type AssignedWorkoutReview,
  type WorkoutAssigned,
} from "@/integrations/backend/workout-assigned";
import {
  completeDateRangeToFilterISO,
  formatGoalDateRangeLabel,
  formatISODateLabelShort,
  formatLocalDateToYYYYMMDD,
  parseYYYYMMDDToLocalDate,
} from "@/lib/date-only";
import { fetchWeeklySummary, type WeeklySummary } from "@/integrations/backend/weekly-summary";
import WorkoutReviewDrawer from "@/components/WorkoutReviewDrawer";
import { ProgramTabEmptyState } from "@/components/client/ProgramTabEmptyState";
import { ScheduleWeekEmptyState } from "@/components/client/ScheduleWeekEmptyState";
import { AiWorkoutGeneratorDialog } from "@/components/client/AiWorkoutGeneratorDialog";

const ClientPlan = () => {
  useEnforceLegalApproval();
  const { profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const autoOpenHandledRef = useRef(false);
  const [activeTab, setActiveTab] = useState<"schedule" | "goals" | "program">("schedule");
  const [weekOffset, setWeekOffset] = useState(0);
  const [workoutAssignments, setWorkoutAssignments] = useState<WorkoutAssigned[]>([]);
  const [loadingWorkouts, setLoadingWorkouts] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewWorkoutId, setReviewWorkoutId] = useState<string | null>(null);
  const [reviewWorkout, setReviewWorkout] = useState<AssignedWorkoutReview | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [programLoading, setProgramLoading] = useState(false);
  const [programError, setProgramError] = useState<string | null>(null);
  const [clientProgram, setClientProgram] = useState<ClientProgram | null>(null);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsError, setGoalsError] = useState<string | null>(null);
  const [onboardingWorkoutFrequency, setOnboardingWorkoutFrequency] = useState<string | null>(null);
  const [weeklyGoalTarget, setWeeklyGoalTarget] = useState<number | null>(null);
  const [joinedAt, setJoinedAt] = useState<Date | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState<Record<string, boolean>>({});
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummary | null>(null);
  const [weeklySummaryLoading, setWeeklySummaryLoading] = useState(false);
  const [showAiDialog, setShowAiDialog] = useState(false);

  // Goals (RHR, Weight, RPE)
  const [goalsSummary, setGoalsSummary] = useState<GoalsSummary | null>(null);
  const [goalsSummaryLoading, setGoalsSummaryLoading] = useState(false);
  const [goalsSummaryError, setGoalsSummaryError] = useState<string | null>(null);

  type MetricKey = "resting_heart_rate" | "body_weight" | "rpe" | string;
  const [metricLogOpen, setMetricLogOpen] = useState<MetricKey | null>(null);
  const [metricDateRange, setMetricDateRange] = useState<DateRange | undefined>(undefined);
  const [metricLoading, setMetricLoading] = useState(false);
  const [metricError, setMetricError] = useState<string | null>(null);
  const [rhrEntries, setRhrEntries] = useState<RestingHrEntry[]>([]);
  const [weightEntries, setWeightEntries] = useState<BodyWeightEntry[]>([]);
  const [rpeEntries, setRpeEntries] = useState<RpeEntry[]>([]);
  const [optionalCatalog, setOptionalCatalog] = useState<GoalMetricCatalogItem[]>([]);
  const [enabledMetricCodes, setEnabledMetricCodes] = useState<string[]>([]);
  const [optionalEntries, setOptionalEntries] = useState<OptionalMetricEntry[]>([]);

  const [addDialogOpen, setAddDialogOpen] = useState<MetricKey | null>(null);
  const [addValue, setAddValue] = useState<string>("");
  const [addDate, setAddDate] = useState<string>(() => format(new Date(), "yyyy-MM-dd"));
  const [addVariant, setAddVariant] = useState<string>("3");
  const [addSaving, setAddSaving] = useState(false);
  
  // Calculate the current week's start and end date
  const startOfCurrentWeek = startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 0 });
  const endOfCurrentWeek = endOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 0 });
  
  // Sample data for goals
  const goals = [
    { id: 1, title: "Lose 5kg", progress: 60, target: "5kg", current: "3kg", targetDate: "Jul 15" },
    { id: 2, title: "Build core strength", progress: 40, target: "Level 3", current: "Level 1", targetDate: "Aug 30" },
    { id: 3, title: "Run 5km", progress: 75, target: "5km", current: "3.75km", targetDate: "Jun 20" }
  ];
  
  // Get week label based on weekOffset
  const getWeekLabel = (): string => {
    if (weekOffset === 0) return "This Week's Workouts";
    if (weekOffset === -1) return "Last Week's Workouts";
    if (weekOffset === 1) return "Next Week's Workouts";
    return "Workouts";
  };

  // Filter workouts by selected date range
  const workouts = useMemo(() => {
    const weekStartStr = formatLocalDateToYYYYMMDD(startOfCurrentWeek);
    const weekEndStr = formatLocalDateToYYYYMMDD(endOfCurrentWeek);
    
    return workoutAssignments
      .filter((w) => {
        // Compare date strings directly (YYYY-MM-DD format)
        return w.date >= weekStartStr && w.date <= weekEndStr;
      })
      .sort((a, b) => {
        // Sort by date ascending
        return a.date.localeCompare(b.date);
      })
      .map((w) => ({
        id: w.id,
        title: w.name,
        date: parseYYYYMMDDToLocalDate(w.date),
        duration: `${w.duration_minutes} min`,
        completed: w.completed_workout !== null
      }));
  }, [workoutAssignments, startOfCurrentWeek, endOfCurrentWeek]);
  
  // Go to previous week
  const previousWeek = () => {
    setWeekOffset(weekOffset - 1);
  };
  
  // Go to next week
  const nextWeek = () => {
    setWeekOffset(weekOffset + 1);
  };
  
  // Go to current week
  const currentWeek = () => {
    setWeekOffset(0);
  };
  
  const loadProgram = useCallback(async () => {
    try {
      setProgramLoading(true);
      setProgramError(null);
      const cp = await getMyCurrentProgram();
      setClientProgram(cp);
    } catch (e: any) {
      setProgramError(e?.message || "Failed to load program");
      setClientProgram(null);
    } finally {
      setProgramLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProgram();
  }, [loadProgram]);

  // Fetch scheduled workouts for the current client
  useEffect(() => {
    if (!profile?.id) return;
    
    let active = true;
    (async () => {
      try {
        setLoadingWorkouts(true);
        const assignments = await listWorkoutAssigned(profile.id);
        if (active) {
          setWorkoutAssignments(assignments);
        }
      } catch (error) {
        console.error("Failed to load scheduled workouts", error);
        if (active) {
          setWorkoutAssignments([]);
        }
      } finally {
        if (active) {
          setLoadingWorkouts(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.id]);

  const loadGoalsMeta = useCallback(async () => {
    try {
      setGoalsLoading(true);
      setGoalsError(null);
      const [onboarding, profile] = await Promise.all([getOnboarding(), getMyProfile()]);
      setOnboardingWorkoutFrequency(onboarding.workoutFrequency ?? null);
      setWeeklyGoalTarget(workoutFrequencyToWeeklyGoal(onboarding.workoutFrequency) ?? null);
      setJoinedAt(profile.created_at ? new Date(profile.created_at) : null);
    } catch (e: any) {
      setGoalsError(e?.message || "Failed to load goal settings");
    } finally {
      setGoalsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadGoalsMeta();
  }, [loadGoalsMeta]);

  const loadGoalsSummary = useCallback(async () => {
    try {
      setGoalsSummaryLoading(true);
      setGoalsSummaryError(null);
      const s = await getGoalsSummary();
      setGoalsSummary(s);
    } catch (e: any) {
      setGoalsSummary(null);
      setGoalsSummaryError(e?.message || "Failed to load goals");
    } finally {
      setGoalsSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadGoalsSummary();
  }, [loadGoalsSummary]);

  const loadOptionalMeta = useCallback(async () => {
    try {
      const [catalog, enabled] = await Promise.all([listAvailableGoalMetrics(), getEnabledGoalMetrics()]);
      setOptionalCatalog(catalog);
      setEnabledMetricCodes(enabled.metric_codes || []);
    } catch {
      setOptionalCatalog([]);
      setEnabledMetricCodes([]);
    }
  }, []);

  useEffect(() => {
    void loadOptionalMeta();
  }, [loadOptionalMeta]);

  const loadWeeklySummary = useCallback(async () => {
    try {
      setWeeklySummaryLoading(true);
      const s = await fetchWeeklySummary();
      setWeeklySummary(s);
    } catch {
      setWeeklySummary(null);
    } finally {
      setWeeklySummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWeeklySummary();
  }, [loadWeeklySummary]);

  const metricRangeISO = useMemo(
    () => completeDateRangeToFilterISO(metricDateRange),
    [metricDateRange]
  );

  const openMetricLog = useCallback((metric: MetricKey) => {
    setMetricDateRange(undefined);
    setMetricLogOpen(metric);
  }, []);

  const loadMetricLog = useCallback(async (metric: MetricKey) => {
    try {
      setMetricLoading(true);
      setMetricError(null);
      const { from, to } = metricRangeISO;
      if (metric === "resting_heart_rate") {
        setRhrEntries(await listRestingHr(from, to));
      } else if (metric === "body_weight") {
        setWeightEntries(await listBodyWeight(from, to));
      } else if (metric === "rpe") {
        setRpeEntries(await listRpe(from, to));
      } else {
        setOptionalEntries(await listOptionalMetricEntries(metric, from, to));
      }
    } catch (e: any) {
      setMetricError(e?.message || "Failed to load log");
    } finally {
      setMetricLoading(false);
    }
  }, [metricRangeISO]);

  useEffect(() => {
    if (!metricLogOpen) return;
    void loadMetricLog(metricLogOpen);
  }, [metricLogOpen, metricRangeISO.from, metricRangeISO.to, loadMetricLog]);

  const loadWorkoutReview = useCallback(async (assignedWorkoutId: string) => {
    try {
      setReviewLoading(true);
      setReviewError(null);
      const review = await fetchAssignedWorkoutReview(assignedWorkoutId);
      setReviewWorkout(review);
    } catch (e: any) {
      setReviewWorkout(null);
      setReviewError(e?.message || "Failed to load workout review");
    } finally {
      setReviewLoading(false);
    }
  }, []);

  const openWorkoutReview = useCallback((assignedWorkoutId: string) => {
    setReviewWorkoutId(assignedWorkoutId);
    setReviewWorkout(null);
    setReviewError(null);
    setReviewOpen(true);
    void loadWorkoutReview(assignedWorkoutId);
  }, [loadWorkoutReview]);

  const retryWorkoutReview = useCallback(() => {
    if (!reviewWorkoutId) return;
    void loadWorkoutReview(reviewWorkoutId);
  }, [loadWorkoutReview, reviewWorkoutId]);

  const openAddDialog = useCallback((metric: MetricKey) => {
    setAddDialogOpen(metric);
    setAddValue("");
    setAddDate(format(new Date(), "yyyy-MM-dd"));
    setAddVariant("3");
  }, []);

  const saveAddDialog = useCallback(async () => {
    if (!addDialogOpen) return;
    try {
      setAddSaving(true);
      const v = Number(addValue);
      if (!Number.isFinite(v)) {
        throw new Error("Please enter a number");
      }
      if (addDialogOpen === "resting_heart_rate") {
        await createRestingHr(Math.round(v), addDate);
      } else if (addDialogOpen === "body_weight") {
        await createBodyWeight(v, addDate);
      } else if (addDialogOpen === "rpe") {
        return;
      } else {
        const item = optionalCatalog.find((c) => c.code === addDialogOpen);
        const variant = item?.supports_variant ? Number(addVariant) : undefined;
        await createOptionalMetricEntry(addDialogOpen, v, addDate, variant);
      }
      setAddDialogOpen(null);
      await loadGoalsSummary();
      await loadOptionalMeta();
      if (metricLogOpen === addDialogOpen) {
        await loadMetricLog(addDialogOpen);
      }
    } catch (e: any) {
      setMetricError(e?.message || "Failed to save");
    } finally {
      setAddSaving(false);
    }
  }, [addDate, addDialogOpen, addValue, addVariant, loadGoalsSummary, loadOptionalMeta, loadMetricLog, metricLogOpen, optionalCatalog]);

  const formatDelta = useCallback((n: number | null, unit: string) => {
    if (n === null || n === undefined) return "—";
    const sign = n > 0 ? "+" : "";
    return `${sign}${Number(n).toFixed(unit === "lbs" ? 1 : 0)} ${unit}`;
  }, []);

  const motivationLabels = useMemo(() => {
    const ids = clientProgram?.program?.motivations ?? [];
    return ids.map((id) => labels.motivation[id] ?? id);
  }, [clientProgram]);

  const exerciseTypeLabels = useMemo(() => {
    const ids = clientProgram?.program?.exercise_types ?? [];
    return ids.map((id) => labels.exerciseType[id] ?? id);
  }, [clientProgram]);

  const weeklyGoal = weeklySummary?.weekly_goal ?? weeklyGoalTarget ?? 3;
  const completedWorkoutsThisWeek = weeklySummary?.current_week?.completed ?? 0;
  const remainingWorkouts = Math.max(weeklyGoal - completedWorkoutsThisWeek, 0);

  const weeklySummaries: WeekSummary[] = useMemo(() => {
    const now = new Date();
    const join = joinedAt ?? now;
    const completedByWeekStartISO = weeklySummary?.completed_by_week_start ?? {};
    return buildWeeklySummariesSinceJoin({
      joinedAt: join,
      now,
      weeklyGoal,
      completedByWeekStartISO,
    });
  }, [joinedAt, weeklyGoal, weeklySummary?.completed_by_week_start]);

  const currentStreakWeeks = weeklySummary?.current_streak_weeks ?? computeCurrentStreakWeeks(weeklySummaries);
  const bestStreakWeeks = weeklySummary?.best_streak_weeks ?? computeBestStreakWeeks(weeklySummaries);
  const metPercent = computeMetPercentage(weeklySummaries);
  const joinedLabel = useMemo(() => (joinedAt ? format(joinedAt, "MMM d, yyyy") : "—"), [joinedAt]);

  const weekGroups = useMemo(() => {
    const groups = new Map<string, { key: string; label: string; weeks: WeekSummary[] }>();
    for (const w of weeklySummaries) {
      const key = format(w.weekStart, "yyyy-MM");
      const existing = groups.get(key);
      if (existing) {
        existing.weeks.push(w);
      } else {
        groups.set(key, { key, label: format(w.weekStart, "MMMM yyyy"), weeks: [w] });
      }
    }
    return Array.from(groups.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
  }, [weeklySummaries]);

  const toggleWeekExpanded = useCallback((weekKey: string) => {
    setExpandedWeeks((prev) => ({ ...prev, [weekKey]: !prev[weekKey] }));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab === "schedule" || tab === "goals" || tab === "program") {
      setActiveTab(tab);
    }

    const wantsOpen =
      params.get("openWorkoutGoalHistory") === "1" ||
      (location.state as any)?.openWorkoutGoalHistory === true;

    if (wantsOpen && !autoOpenHandledRef.current) {
      autoOpenHandledRef.current = true;
      setActiveTab("goals");
      setHistoryOpen(true);
      // Clean up one-time param so closing the drawer doesn’t immediately reopen it.
      params.delete("openWorkoutGoalHistory");
      navigate(`${location.pathname}?${params.toString()}`, { replace: true, state: {} as any });
    }
  }, [location.pathname, location.search, location.state, navigate]);
  
  return (
    <ClientPageLayout
      activeTab="plan"
      header={
        <>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Your Training Plan</h1>
            <ProfileIconLink />
          </div>

          <button
            type="button"
            onClick={() => {
              setActiveTab("goals");
              setHistoryOpen(true);
              navigate(`${location.pathname}?tab=goals&openWorkoutGoalHistory=1`, { replace: false });
            }}
            className="w-full text-left bg-dark-slate/50 backdrop-blur-sm rounded-2xl p-4 border border-primary/20 hover:border-primary/40 hover:bg-dark-slate/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label="View this week's goal and workout goal history"
          >
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-white">This week's goal</span>
              <span className="text-xs bg-primary text-black rounded-full px-3 py-1 font-semibold">
                {weeklySummaryLoading ? "—" : `${completedWorkoutsThisWeek}/${weeklyGoal} workouts`}
              </span>
            </div>
            <Progress value={Math.min(100, (completedWorkoutsThisWeek / weeklyGoal) * 100)} className="h-3" />
            <p className="text-xs text-gray-light mt-1">
              {remainingWorkouts === 0 ? "Goal reached this week 🎉" : `${remainingWorkouts} more ${remainingWorkouts === 1 ? "workout" : "workouts"} to reach your weekly goal`}
            </p>
          </button>
        </>
      }
    >
        {/* Tabs for plan sections */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
            <TabsTrigger value="goals">Goals</TabsTrigger>
            <TabsTrigger value="program">Program</TabsTrigger>
          </TabsList>
          
          {/* Schedule tab */}
          <TabsContent value="schedule" className="space-y-6">
            {/* Calendar navigation */}
            <div className="flex items-center justify-between mb-2">
              <Button variant="ghost" size="sm" onClick={previousWeek} aria-label="Previous week">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-center">
                <h3 className="font-medium text-white">
                  {format(startOfCurrentWeek, "MMM d")} - {format(endOfCurrentWeek, "MMM d")}
                </h3>
                {weekOffset !== 0 && (
                  <Button variant="link" size="sm" onClick={currentWeek} className="text-xs p-0 h-auto">
                    Go to current week
                  </Button>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={nextWeek} aria-label="Next week">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Today's workouts */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg flex items-center text-white">
                <CalendarDays className="h-5 w-5 mr-2 text-primary" />
                {getWeekLabel()}
              </h3>
              
              {loadingWorkouts ? (
                <div className="text-sm text-gray-light py-4 text-center">Loading workouts...</div>
              ) : workouts.length === 0 ? (
                <ScheduleWeekEmptyState
                  weekOffset={weekOffset}
                  remainingWorkouts={remainingWorkouts}
                  weeklyGoal={weeklyGoal}
                  onBrowseTemplates={() => navigate("/explore#workouts")}
                  onOpenAiGenerator={() => setShowAiDialog(true)}
                />
              ) : (
                workouts.map(workout => (
                  <Card key={workout.id} className={`border-primary/20 shadow-card overflow-hidden bg-dark-card ${workout.completed ? "opacity-90" : ""}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center">
                            <h4 className="font-medium text-white">{workout.title}</h4>
                            {workout.completed && (
                              <CheckCircle className="h-4 w-4 text-primary ml-2" />
                            )}
                          </div>
                          <div className="text-sm text-gray-light">
                            {format(workout.date, "EEEE, MMMM d")}
                          </div>
                          <div className="flex mt-2 items-center">
                            <Badge variant="default" className="mr-2 bg-primary/20 text-primary border-primary/30">
                              {workout.duration}
                            </Badge>
                          </div>
                        </div>
                        <Button 
                          variant={workout.completed ? "outline" : "default"}
                          size="sm"
                          className={workout.completed ? "text-gray-light" : ""}
                          onClick={() => {
                            if (workout.completed) {
                              openWorkoutReview(workout.id);
                            } else {
                              navigate(`/start-workout?assigned=${workout.id}`);
                            }
                          }}
                        >
                          {workout.completed ? "Review" : "Start"}
                          <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
          
          {/* Goals tab */}
          <TabsContent value="goals" className="space-y-6">
            <Card className="border-primary/20 shadow-card overflow-hidden bg-dark-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between gap-3 text-white">
                  <span className="flex items-center">
                    <Target className="h-5 w-5 mr-2" />
                    Weekly Goal
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setHistoryOpen(true)}
                    className="text-primary hover:bg-primary/10"
                  >
                    View history
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {goalsLoading ? (
                  <div className="space-y-3">
                    <div className="h-6 w-40 rounded bg-white/10 animate-pulse" />
                    <div className="h-3 w-full rounded bg-white/10 animate-pulse" />
                    <div className="h-3 w-2/3 rounded bg-white/10 animate-pulse" />
                  </div>
                ) : goalsError ? (
                  <div className="space-y-2">
                    <p className="text-gray-light">We couldn’t load your goal settings right now.</p>
                    <p className="text-xs text-gray-light/80 break-words">{goalsError}</p>
                    <Button
                      variant="outline"
                      onClick={() => void loadGoalsMeta()}
                      className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                    >
                      Retry
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-end justify-between gap-4">
                      <div>
                        <p className="text-sm text-gray-light">This week</p>
                        <div className="flex items-baseline gap-2">
                          <p className="text-3xl font-bold text-white">
                            {weeklySummaryLoading ? "—" : completedWorkoutsThisWeek}
                          </p>
                          <p className="text-sm text-gray-light">/ {weeklyGoal} workouts</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="border-primary/30 text-white">
                        {remainingWorkouts === 0 ? "Goal hit" : `${remainingWorkouts} left`}
                      </Badge>
                    </div>

                    <Progress value={Math.min(100, (completedWorkoutsThisWeek / weeklyGoal) * 100)} className="h-3" />

                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-light">
                        {onboardingWorkoutFrequency
                          ? `From onboarding: ${labels.workoutFrequency[onboardingWorkoutFrequency] ?? "Weekly goal"}`
                          : "Set your weekly goal in onboarding to start tracking streaks."}
                      </p>
                      {remainingWorkouts === 0 ? (
                        <p className="text-xs text-primary">Streak protected for this week</p>
                      ) : (
                        <p className="text-xs text-gray-light/80">Finish strong by Saturday</p>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="border-primary/20 shadow-card overflow-hidden bg-dark-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between gap-3 text-white">
                  <span>Your Fitness Goals</span>
                  <Badge variant="outline" className="border-primary/30 text-white">
                    Metrics
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-light">
                  Track Resting Heart Rate, Body Weight, and RPE (how hard workouts felt).
                </p>
              </CardContent>
            </Card>

            <Card className="border-primary/20 shadow-card overflow-hidden bg-dark-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-white">Your Goal Tracking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {goalsSummaryLoading ? (
                  <div className="space-y-3">
                    <div className="h-6 w-48 rounded bg-white/10 animate-pulse" />
                    <div className="h-20 w-full rounded bg-white/10 animate-pulse" />
                    <div className="h-20 w-full rounded bg-white/10 animate-pulse" />
                    <div className="h-20 w-full rounded bg-white/10 animate-pulse" />
                  </div>
                ) : goalsSummaryError ? (
                  <div className="space-y-2">
                    <p className="text-gray-light">We couldn’t load your goal tracking right now.</p>
                    <p className="text-xs text-gray-light/80 break-words">{goalsSummaryError}</p>
                    <Button
                      variant="outline"
                      onClick={() => void loadGoalsSummary()}
                      className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                    >
                      Retry
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {([
                      {
                        key: "resting_heart_rate" as const,
                        title: "Resting Heart Rate",
                        icon: HeartPulse,
                        unit: "bpm",
                        allowAdd: true,
                      },
                      {
                        key: "body_weight" as const,
                        title: "Body Weight",
                        icon: Scale,
                        unit: "lbs",
                        allowAdd: true,
                      },
                      {
                        key: "rpe" as const,
                        title: "RPE",
                        icon: Activity,
                        unit: "rpe",
                        allowAdd: false,
                      },
                      ...optionalCatalog
                        .filter((c) => enabledMetricCodes.includes(c.code))
                        .map((c) => ({
                          key: c.code as MetricKey,
                          title: c.label,
                          icon: Activity,
                          unit: c.unit,
                          allowAdd: true,
                        })),
                    ] as const).map((m) => {
                      const s =
                        m.key === "resting_heart_rate" || m.key === "body_weight" || m.key === "rpe"
                          ? (goalsSummary as any)?.[m.key]
                          : goalsSummary?.optional?.[m.key];
                      const start = s?.start ?? null;
                      const current = s?.current ?? null;
                      const ds = s?.delta_since_start ?? null;
                      const dl = s?.delta_since_last ?? null;
                      const unit = m.unit;
                      const Icon = m.icon;
                      return (
                        <div
                          key={m.key}
                          className="rounded-2xl border border-primary/20 bg-white/5 p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <Icon className="h-5 w-5 text-primary" />
                                <p className="text-sm font-semibold text-white">{m.title}</p>
                              </div>
                              <p className="mt-1 text-xs text-gray-light">
                                {m.key === "rpe"
                                  ? "Recorded after each completed workout."
                                  : "Add a measurement anytime (defaults to today)."}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {m.allowAdd ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openAddDialog(m.key)}
                                  className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                                >
                                  Add
                                </Button>
                              ) : null}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openMetricLog(m.key)}
                                className="text-primary hover:bg-primary/10"
                              >
                                View log
                              </Button>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-2 gap-3">
                            <div className="rounded-xl bg-black/20 p-3 border border-primary/10">
                              <p className="text-[11px] text-gray-light">Start</p>
                              <p className="text-lg font-semibold text-white">
                                {start === null ? "—" : `${start} ${unit === "rpe" ? "" : unit}`.trim()}
                              </p>
                            </div>
                            <div className="rounded-xl bg-black/20 p-3 border border-primary/10">
                              <p className="text-[11px] text-gray-light">Current</p>
                              <p className="text-lg font-semibold text-white">
                                {current === null ? "—" : `${current} ${unit === "rpe" ? "" : unit}`.trim()}
                              </p>
                            </div>
                            <div className="rounded-xl bg-black/20 p-3 border border-primary/10">
                              <p className="text-[11px] text-gray-light">Since start</p>
                              <p className={cn("text-sm font-semibold", ds === null ? "text-gray-light" : "text-white")}>
                                {unit === "rpe" ? (ds === null ? "—" : `${ds > 0 ? "+" : ""}${ds}`) : formatDelta(ds as any, unit)}
                              </p>
                            </div>
                            <div className="rounded-xl bg-black/20 p-3 border border-primary/10">
                              <p className="text-[11px] text-gray-light">Since last</p>
                              <p className={cn("text-sm font-semibold", dl === null ? "text-gray-light" : "text-white")}>
                                {unit === "rpe" ? (dl === null ? "—" : `${dl > 0 ? "+" : ""}${dl}`) : formatDelta(dl as any, unit)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Drawer open={historyOpen} onOpenChange={setHistoryOpen}>
              <DrawerContent className="bg-dark-card text-white border-primary/20">
                <DrawerHeader className="text-left">
                  <DrawerTitle>Workout Goal History</DrawerTitle>
                  <DrawerDescription className="text-gray-light">
                    Joined {joinedLabel} • Weeks tracked {weeklySummaries.length} • Goal met {metPercent}%
                  </DrawerDescription>
                </DrawerHeader>

                <div className="px-4 pb-6 space-y-5 overflow-y-auto max-h-[70vh]">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-xl bg-white/5 p-3 border border-primary/10">
                      <p className="text-[11px] text-gray-light">Current streak</p>
                      <p className="text-lg font-semibold text-white">{currentStreakWeeks}w</p>
                    </div>
                    <div className="rounded-xl bg-white/5 p-3 border border-primary/10">
                      <p className="text-[11px] text-gray-light">Best streak</p>
                      <p className="text-lg font-semibold text-white">{bestStreakWeeks}w</p>
                    </div>
                    <div className="rounded-xl bg-white/5 p-3 border border-primary/10">
                      <p className="text-[11px] text-gray-light">Weekly goal</p>
                      <p className="text-lg font-semibold text-white">{weeklyGoal}</p>
                    </div>
                  </div>

                  {weekGroups.map((g) => (
                    <div key={g.key} className="space-y-2">
                      <p className="text-sm font-medium text-white">{g.label}</p>
                      <div className="space-y-2">
                        {g.weeks
                          .slice()
                          .sort((a, b) => (a.weekStart < b.weekStart ? 1 : -1))
                          .map((w) => {
                            const weekKey = format(w.weekStart, "yyyy-MM-dd");
                            const expanded = !!expandedWeeks[weekKey];
                            return (
                              <div key={weekKey} className="rounded-xl border border-primary/10 bg-white/5">
                                <button
                                  type="button"
                                  onClick={() => toggleWeekExpanded(weekKey)}
                                  className="w-full px-3 py-3 flex items-center justify-between gap-3 text-left"
                                >
                                  <div className="min-w-0">
                                    <p className="text-sm text-white">
                                      {format(w.weekStart, "MMM d")}–{format(w.weekEnd, "MMM d")}
                                    </p>
                                    <p className="text-xs text-gray-light">
                                      {w.completed}/{w.goal} workouts
                                    </p>
                                  </div>
                                  <Badge
                                    variant="outline"
                                    className={
                                      w.met
                                        ? "border-primary/30 text-primary bg-primary/10"
                                        : "border-primary/20 text-gray-light"
                                    }
                                  >
                                    {w.met ? "Met" : "Missed"}
                                  </Badge>
                                </button>

                                {expanded ? (
                                  <div className="px-3 pb-3 pt-0 text-xs text-gray-light/90">
                                    {w.completed > 0 ? (
                                      <p>Workout details will appear here once backend tracking is connected.</p>
                                    ) : (
                                      <p>No workouts logged this week.</p>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ))}
                </div>
              </DrawerContent>
            </Drawer>

            <Drawer
              open={metricLogOpen !== null}
              onOpenChange={(open) => {
                if (!open) {
                  setMetricLogOpen(null);
                  setMetricDateRange(undefined);
                }
              }}
            >
              <DrawerContent className="bg-dark-card text-white border-primary/20">
                <DrawerHeader className="text-left">
                  <DrawerTitle>
                    {metricLogOpen === "resting_heart_rate"
                      ? "Resting Heart Rate Log"
                      : metricLogOpen === "body_weight"
                        ? "Body Weight Log"
                        : metricLogOpen === "rpe"
                          ? "RPE Log"
                          : optionalCatalog.find((c) => c.code === metricLogOpen)?.label || "Goal Log"}
                  </DrawerTitle>
                  <DrawerDescription className="text-gray-light">
                    Filter by date range, view trends, and review entries.
                  </DrawerDescription>
                </DrawerHeader>

                <div className="px-4 pb-6 space-y-4 overflow-y-auto max-h-[75vh]">
                  <div className="flex items-center justify-between gap-2">
                    <Popover modal={false}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="border-primary/30 text-white hover:bg-primary hover:text-black"
                        >
                          {formatGoalDateRangeLabel(metricDateRange)}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-dark-card border border-primary/20" align="start">
                        <DateRangeCalendar mode="range" numberOfMonths={2} selected={metricDateRange} onSelect={setMetricDateRange} />
                      </PopoverContent>
                    </Popover>
                    {metricDateRange?.from ? (
                      <Button variant="ghost" className="text-gray-light hover:text-white" onClick={() => setMetricDateRange(undefined)}>
                        Reset
                      </Button>
                    ) : null}
                  </div>

                  {metricError ? <p className="text-xs text-red-300">{metricError}</p> : null}

                  {metricLoading ? (
                    <div className="space-y-3">
                      <div className="h-40 w-full rounded bg-white/10 animate-pulse" />
                      <div className="h-24 w-full rounded bg-white/10 animate-pulse" />
                    </div>
                  ) : (
                    <>
                      {/* Chart */}
                      <Card className="border-primary/20 bg-white/5">
                        <CardContent className="p-3">
                          {(() => {
                            const data =
                              metricLogOpen === "resting_heart_rate"
                                ? rhrEntries.map((e) => ({ date: e.recorded_on || "", value: e.bpm }))
                                : metricLogOpen === "body_weight"
                                  ? weightEntries.map((e) => ({ date: e.recorded_on || "", value: e.weight_lbs }))
                                  : metricLogOpen === "rpe"
                                    ? rpeEntries.map((e) => ({ date: e.date || "", value: e.rpe }))
                                    : optionalEntries.map((e) => ({ date: e.recorded_on || "", value: e.value }));
                            if (data.length === 0) {
                              return <div className="text-sm text-gray-light py-6 text-center">No entries yet.</div>;
                            }
                            const yDomain =
                              metricLogOpen === "rpe" ? ([1, 10] as any) : (["auto", "auto"] as any);
                            return (
                              <ChartContainer
                                config={{ metric: { label: "Metric", color: "hsl(73 100% 60%)" } }}
                                className="h-44 w-full"
                              >
                                <LineChart data={data}>
                                  <CartesianGrid strokeDasharray="4 4" stroke="#2E2E2E" />
                                  <XAxis
                                    dataKey="date"
                                    tickLine={false}
                                    axisLine={false}
                                    tickMargin={8}
                                    stroke="#6F6F6F"
                                    tickFormatter={(v) => formatISODateLabelShort(String(v))}
                                  />
                                  <YAxis domain={yDomain} tickLine={false} axisLine={false} tickMargin={8} stroke="#6F6F6F" />
                                  <ChartTooltip content={<ChartTooltipContent labelKey="metric" />} />
                                  <Line type="monotone" dataKey="value" stroke="var(--color-metric)" strokeWidth={3} dot={{ r: 3 }} />
                                </LineChart>
                              </ChartContainer>
                            );
                          })()}
                        </CardContent>
                      </Card>

                      {/* Table */}
                      <Card className="border-primary/20 bg-white/5">
                        <CardContent className="p-3">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-primary/10">
                                <TableHead className="text-gray-light">Date</TableHead>
                                <TableHead className="text-gray-light">Value</TableHead>
                                <TableHead className="text-gray-light">Entered by</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(metricLogOpen === "resting_heart_rate"
                                ? [...rhrEntries].reverse().map((e) => ({
                                    key: e.id,
                                    date: e.recorded_on,
                                    value: `${e.bpm} bpm`,
                                    by: e.created_by?.name ?? "—",
                                  }))
                                : metricLogOpen === "body_weight"
                                  ? [...weightEntries].reverse().map((e) => ({
                                      key: e.id,
                                      date: e.recorded_on,
                                      value: `${e.weight_lbs} lbs`,
                                      by: e.created_by?.name ?? "—",
                                    }))
                                  : metricLogOpen === "rpe"
                                    ? [...rpeEntries].reverse().map((e) => ({
                                        key: e.workout_assigned_id,
                                        date: e.date,
                                        value: `${e.rpe} / 10`,
                                        by: "App",
                                      }))
                                    : [...optionalEntries].reverse().map((e) => ({
                                        key: e.id,
                                        date: e.recorded_on,
                                        value: `${e.value}${e.variant ? ` (x${e.variant})` : ""}`,
                                        by: e.created_by?.name ?? "—",
                                      }))
                              ).map((row) => (
                                <TableRow key={row.key} className="border-primary/10">
                                  <TableCell className="text-white">{row.date || "—"}</TableCell>
                                  <TableCell className="text-white">{row.value}</TableCell>
                                  <TableCell className="text-gray-light">{row.by}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>
              </DrawerContent>
            </Drawer>

            <Dialog open={addDialogOpen !== null} onOpenChange={(open) => !open && setAddDialogOpen(null)}>
              <DialogContent className="bg-dark-card text-white border-primary/20">
                <UIDialogHeader>
                  <UIDialogTitle>
                    {addDialogOpen === "resting_heart_rate"
                      ? "Add Resting Heart Rate"
                      : addDialogOpen === "body_weight"
                        ? "Add Body Weight"
                        : addDialogOpen === "rpe"
                          ? "Add"
                          : optionalCatalog.find((c) => c.code === addDialogOpen)?.label || "Add"}
                  </UIDialogTitle>
                </UIDialogHeader>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-light mb-1">Value</p>
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={addValue}
                      onChange={(e) => setAddValue(e.target.value)}
                      placeholder={
                        addDialogOpen === "resting_heart_rate"
                          ? "e.g. 55"
                          : addDialogOpen === "body_weight"
                            ? "e.g. 180.5"
                            : "e.g. 10"
                      }
                      className="bg-dark-slate border-primary/20 text-white"
                    />
                  </div>
                  {addDialogOpen &&
                  addDialogOpen !== "resting_heart_rate" &&
                  addDialogOpen !== "body_weight" &&
                  addDialogOpen !== "rpe" &&
                  optionalCatalog.find((c) => c.code === addDialogOpen)?.supports_variant ? (
                    <div>
                      <p className="text-xs text-gray-light mb-1">Variant</p>
                      <select
                        value={addVariant}
                        onChange={(e) => setAddVariant(e.target.value)}
                        className="w-full rounded-md bg-dark-slate border border-primary/20 px-3 py-2 text-white"
                      >
                        <option value="3">3RM</option>
                        <option value="5">5RM</option>
                        <option value="8">8RM</option>
                      </select>
                    </div>
                  ) : null}
                  <div>
                    <p className="text-xs text-gray-light mb-1">Date</p>
                    <Input
                      type="date"
                      value={addDate}
                      onChange={(e) => setAddDate(e.target.value)}
                      className="bg-dark-slate border-primary/20 text-white"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setAddDialogOpen(null)} className="text-gray-light hover:text-white">
                    Cancel
                  </Button>
                  <Button onClick={() => void saveAddDialog()} disabled={addSaving} className="bg-primary text-black hover:bg-primary/90">
                    {addSaving ? "Saving..." : "Save"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Program tab */}
          <TabsContent value="program" className="space-y-6">
            {programLoading ? (
              <Card className="border-primary/20 shadow-card overflow-hidden bg-dark-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white">Your Program</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="h-5 w-1/2 rounded bg-white/10 animate-pulse" />
                  <div className="h-4 w-3/4 rounded bg-white/10 animate-pulse" />
                  <div className="h-4 w-2/3 rounded bg-white/10 animate-pulse" />
                </CardContent>
              </Card>
            ) : programError ? (
              <Card className="border-primary/20 shadow-card overflow-hidden bg-dark-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white">Your Program</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-light">We couldn’t load your program right now.</p>
                  <p className="text-xs text-gray-light/80 break-words">{programError}</p>
                  <Button
                    variant="outline"
                    onClick={() => void loadProgram()}
                    className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                  >
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : !clientProgram ? (
              <ProgramTabEmptyState
                onChatWithTrainer={() => navigate("/chat")}
                onViewAssessments={() => navigate("/assessments")}
              />
            ) : (
              <div className="space-y-4">
                <Card className="border-primary/20 shadow-card overflow-hidden bg-dark-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white flex items-center justify-between gap-3">
                      <span className="truncate">{clientProgram.program.name}</span>
                      <Badge variant="outline" className="border-primary/30 text-white">
                        {clientProgram.program.difficulty}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-white">Description</p>
                      {clientProgram.program.description ? (
                        <p className="whitespace-pre-wrap text-gray-light">
                          {clientProgram.program.description}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-light/80">No description provided yet.</p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Badge variant="default" className="bg-primary/20 text-primary border-primary/30">
                        {clientProgram.program.duration} weeks
                      </Badge>
                      <Badge variant="default" className="bg-primary/20 text-primary border-primary/30">
                        {clientProgram.program.frequency} / week
                      </Badge>
                      {clientProgram.assigned_at ? (
                        <Badge variant="outline" className="border-primary/30 text-white">
                          Assigned {format(new Date(clientProgram.assigned_at), "MMM d, yyyy")}
                        </Badge>
                      ) : null}
                    </div>
                    {clientProgram.program.weekly_schedule_preference?.trim() ? (
                      <div>
                        <p className="text-sm font-medium text-white mb-1">Preferred scheduling</p>
                        <p className="text-gray-light">{clientProgram.program.weekly_schedule_preference}</p>
                      </div>
                    ) : null}

                    {(motivationLabels.length > 0 || exerciseTypeLabels.length > 0) && (
                      <div className="space-y-2">
                        {motivationLabels.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-white mb-2">Motivations</p>
                            <div className="flex flex-wrap gap-2">
                              {motivationLabels.map((label) => (
                                <Badge key={label} variant="secondary" className="text-xs">
                                  {label}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {exerciseTypeLabels.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-white mb-2">Exercise Types</p>
                            <div className="flex flex-wrap gap-2">
                              {exerciseTypeLabels.map((label) => (
                                <Badge key={label} variant="secondary" className="text-xs">
                                  {label}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {clientProgram.program.exercise_type_details &&
                      Object.keys(clientProgram.program.exercise_type_details).length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-sm font-medium text-white">Exercise Type Details</p>
                          <div className="space-y-2">
                            {Object.entries(clientProgram.program.exercise_type_details).map(([id, detail]) => {
                              const label = labels.exerciseType[id] ?? id;
                              if (!detail?.trim()) return null;
                              return (
                                <div key={id} className="rounded-lg border border-primary/20 bg-dark-slate p-3">
                                  <div className="text-xs font-semibold text-white">{label}</div>
                                  <div className="text-sm text-gray-light mt-1 whitespace-pre-wrap">{detail}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                  </CardContent>
                </Card>

                {(clientProgram.program.program_focus ||
                  clientProgram.program.what_to_expect ||
                  clientProgram.program.variety) && (
                  <Card className="border-primary/20 shadow-card overflow-hidden bg-dark-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white">What to Expect</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {clientProgram.program.program_focus ? (
                        <div>
                          <p className="text-sm font-medium text-white mb-1">Program focus</p>
                          <p className="text-gray-light">{clientProgram.program.program_focus}</p>
                        </div>
                      ) : null}
                      {clientProgram.program.what_to_expect ? (
                        <div>
                          <p className="text-sm font-medium text-white mb-1">Details</p>
                          <p className="text-gray-light">{clientProgram.program.what_to_expect}</p>
                        </div>
                      ) : null}
                      {clientProgram.program.variety ? (
                        <div>
                          <p className="text-sm font-medium text-white mb-1">Exercise variety</p>
                          <p className="text-gray-light whitespace-pre-wrap">{clientProgram.program.variety}</p>
                        </div>
                      ) : null}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

      <WorkoutReviewDrawer
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        review={reviewWorkout}
        loading={reviewLoading}
        error={reviewError}
        onRetry={retryWorkoutReview}
      />

      <AiWorkoutGeneratorDialog open={showAiDialog} onOpenChange={setShowAiDialog} />
    </ClientPageLayout>
  );
};

export default ClientPlan;
